import * as React from "react"
import { useAuth0 } from "@auth0/auth0-react"
import config from "config"
import { FRESH_TOKEN_DELAY_MS, FRESH_TOKEN_TIMEOUT_S } from "./tokenOptions"
import { ARRIVED_FROM_LOGIN, authErrorCode, signInAgain } from "./sessionEnded"

type Props = {
  delayMs?: number
  // injectable for tests
  arrivedFromLogin?: boolean
  // Whether the SDK can renew at all: localhost has neither a refresh token
  // nor the silent-auth fallback (tokenOptions), so a renewal there only
  // fails — and its error would reach every token call sharing the SDK's
  // in-flight promise, bouncing them to the login page.
  renewable?: boolean
}

/**
 * Renew the access token (and the ID token behind useAuth0().user) once per
 * page load, a few seconds after boot: the page started from a cached token,
 * this brings claims changed in Auth0 since it was minted — and notices an
 * Auth0 session that has ended. See tokenOptions.
 */
export const FreshTokenOnLoad = ({ delayMs = FRESH_TOKEN_DELAY_MS, arrivedFromLogin = ARRIVED_FROM_LOGIN, renewable = !import.meta.env.DEV }: Props) => {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const doneRef = React.useRef(false)

  React.useEffect(() => {
    // Straight back from the login page, the tokens are brand new. Renewing
    // them could also loop wherever the silent-auth iframe can't see the
    // session (third-party cookies blocked for the preview domains):
    // login_required → sign in → back here → login_required …
    if (!renewable || !isAuthenticated || doneRef.current || arrivedFromLogin) return undefined
    const timer = window.setTimeout(() => {
      doneRef.current = true
      getAccessTokenSilently({
        cacheMode: "off",
        // requests made meanwhile wait on this same SDK call (it is shared
        // per audience + scope): don't let a hung iframe hold them for 60 s
        timeoutInSeconds: FRESH_TOKEN_TIMEOUT_S,
        authorizationParams: { audience: config.auth0Audience },
      }).catch((error: unknown) => {
        const code = authErrorCode(error)
        if (code === "login_required") {
          // No Auth0 session any more (signed out elsewhere, blocked, session
          // lifetime over). The SDK has already signed out locally, so the app
          // shows "Authenticating..." — sign in again, back to this page, as a
          // reload used to.
          signInAgain(loginWithRedirect)
          return
        }
        // localhost has no silent-auth fallback: without a refresh token
        // there is nothing to renew with, and the cached token carries on
        if (code === "missing_refresh_token") return
        console.warn("token renewal after load failed; the cached token stays in use", error)
      })
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [renewable, isAuthenticated, getAccessTokenSilently, loginWithRedirect, delayMs, arrivedFromLogin])

  return null
}
